/**
 * Bug Tracker feature area catalog for backend usage.
 *
 * @remarks
 * This copy keeps NodeJs typecheck stable on branches where the submodule
 * snapshot does not yet include `DomainTypes/bugTracker/BugReportFeatureCatalog.ts`.
 *
 * Keep this in sync with:
 * `HelloCashflowDomain/DomainTypes/bugTracker/BugReportFeatureCatalog.ts`.
 *
 * @see docs/features/BUG_TRACKER.md
 */
import {
  BUG_REPORT_FEATURE_CATALOG_DATA,
  BUG_REPORT_TUMU_FEATURE_GROUP_KEYS,
  BUG_REPORT_TUMU_LABEL_DATA,
} from "./bugReportFeatureCatalogData.js";
import type { BugReportFeatureCatalogEntry } from "./bugReportFeatureCatalogData.js";

export type { BugReportFeatureCatalogEntry };

/**
 * monday Bugs board TUMU labels.
 *
 * @remarks
 * The admin bug tracker derives TUMU from the selected feature area instead of
 * inventing a fixed default because the monday workflow uses these statuses as
 * customer-outcome buckets rather than severity labels.
 *
 * See:
 * - docs/features/BUG_TRACKER.md
 */
export const BUG_REPORT_TUMU_LABELS = BUG_REPORT_TUMU_LABEL_DATA;

export type BugReportTumuLabel =
  (typeof BUG_REPORT_TUMU_LABELS)[keyof typeof BUG_REPORT_TUMU_LABELS];

export const BUG_REPORT_FEATURE_CATALOG = BUG_REPORT_FEATURE_CATALOG_DATA;

export type BugReportFeatureKey =
  (typeof BUG_REPORT_FEATURE_CATALOG)[number]["key"];
export type BugReportFeatureCatalogItem =
  (typeof BUG_REPORT_FEATURE_CATALOG)[number];

const BUG_REPORT_FEATURE_KEY_SET: ReadonlySet<string> = new Set(
  BUG_REPORT_FEATURE_CATALOG.map((entry) => entry.key),
);

const BUG_REPORT_TUMU_FEATURES = {
  /**
   * Reporting and exported outputs need to preserve trust in the numbers
   * first. When those features break, the Monday board tracks them under the
   * trust bucket instead of a generic usage bucket.
   */
  trustTheNumbers: new Set<BugReportFeatureKey>(
    BUG_REPORT_TUMU_FEATURE_GROUP_KEYS.trustTheNumbers,
  ),
  /**
   * Planning and scenario tools are primarily about deciding what to do with
   * the numbers next, so they map to the decision-making bucket.
   */
  makeDecisionsFromTheNumbers: new Set<BugReportFeatureKey>(
    BUG_REPORT_TUMU_FEATURE_GROUP_KEYS.makeDecisionsFromTheNumbers,
  ),
  /**
   * Navigation, setup, and ongoing-product-use surfaces are grouped under the
   * usage bucket. We intentionally leave "Understand the numbers" unset until
   * there is stronger workflow evidence for a stable feature-key mapping.
   */
  useHcRegularly: new Set<BugReportFeatureKey>(
    BUG_REPORT_TUMU_FEATURE_GROUP_KEYS.useHcRegularly,
  ),
} as const;

export const isBugReportFeatureKey = (
  value: string,
): value is BugReportFeatureKey => BUG_REPORT_FEATURE_KEY_SET.has(value);

export const getBugReportFeatureCatalogItem = (
  featureKey: string | null | undefined,
): BugReportFeatureCatalogItem | null => {
  const trimmed = (featureKey ?? "").toString().trim();
  if (!trimmed) return null;
  if (!isBugReportFeatureKey(trimmed)) return null;

  for (const entry of BUG_REPORT_FEATURE_CATALOG) {
    if (entry.key === trimmed) {
      return entry;
    }
  }

  return null;
};

export const getBugReportFeatureOptions = (): BugReportFeatureCatalogEntry[] =>
  BUG_REPORT_FEATURE_CATALOG.map((entry) => ({
    key: entry.key,
    label: entry.label,
    userHelp: entry.userHelp,
    aiBrief: entry.aiBrief,
  }));

export const resolveBugReportTumuLabel = (
  featureKey: string | null | undefined,
): BugReportTumuLabel | null => {
  const feature = getBugReportFeatureCatalogItem(featureKey);
  if (!feature) return null;

  if (BUG_REPORT_TUMU_FEATURES.trustTheNumbers.has(feature.key)) {
    return BUG_REPORT_TUMU_LABELS.trustTheNumbers;
  }

  if (BUG_REPORT_TUMU_FEATURES.makeDecisionsFromTheNumbers.has(feature.key)) {
    return BUG_REPORT_TUMU_LABELS.makeDecisionsFromTheNumbers;
  }

  if (BUG_REPORT_TUMU_FEATURES.useHcRegularly.has(feature.key)) {
    return BUG_REPORT_TUMU_LABELS.useHcRegularly;
  }

  return null;
};
